const BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export function mediaUrl(path?: string | null) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${BASE}${p}`;
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const token = localStorage.getItem('sb-token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE}${path}`, {
    method: options.method || 'GET',
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'API Error');
  }

  return res.json();
}

// Public API
export const api = {
  // Public
  services: () => request<any[]>('/api/public/services'),
  portfolio: (params?: { category?: string; search?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.search) qs.set('search', params.search);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    return request<any>(`/api/public/portfolio?${qs}`);
  },
  portfolioItem: (slug: string) => request<any>(`/api/public/portfolio/${slug}`),
  heroVideo: () => request<{ public_path: string; media_id: string }>('/api/public/media/hero-video'),
  mediaPlayUrl: (params: { publicPath?: string; mediaId?: string }) => {
    const qs = new URLSearchParams();
    if (params.publicPath) qs.set('publicPath', params.publicPath);
    if (params.mediaId) qs.set('mediaId', params.mediaId);
    return request<{ url: string; expiresAt: number }>(`/api/public/media/play-url?${qs}`);
  },
  categories: () => request<any[]>('/api/public/categories'),
  navigation: () => request<any[]>('/api/public/navigation'),
  faq: () => request<any[]>('/api/public/faq'),
  testimonials: () => request<any[]>('/api/public/testimonials'),
  steps: () => request<any[]>('/api/public/steps'),
  contacts: () => request<any>('/api/public/contacts'),
  footer: () => request<any[]>('/api/public/footer'),
  page: (slug: string) => request<any>(`/api/public/pages/${slug}`),
  design: () => request<any>('/api/public/design'),

  // Auth
  login: (email: string, password: string) =>
    request<{ token: string; user: any }>('/api/admin/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
  me: () => request<any>('/api/admin/auth/me'),

  // Admin
  admin: {
    portfolio: {
      list: (params?: { status?: string; limit?: number; offset?: number }) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        return request<any>(`/api/admin/portfolio?${qs}`);
      },
      get: (id: string) => request<any>(`/api/admin/portfolio/${id}`),
      create: (data: any) => request<any>('/api/admin/portfolio', { method: 'POST', body: data }),
      update: (id: string, data: any) => request<any>(`/api/admin/portfolio/${id}`, { method: 'PUT', body: data }),
      delete: (id: string) => request<any>(`/api/admin/portfolio/${id}`, { method: 'DELETE' }),
      setCategories: (id: string, category_ids: string[]) =>
        request<any>(`/api/admin/portfolio/${id}/categories`, { method: 'PUT', body: { category_ids } }),
      setMedia: (id: string, items: { media_id: string; sort_order?: number; role?: string }[]) =>
        request<any>(`/api/admin/portfolio/${id}/media`, { method: 'PUT', body: { items } }),
      listMedia: (id: string) => request<any[]>(`/api/admin/portfolio/${id}/media`),
    },
    media: {
      list: (params?: {
        folder?: string;
        type?: string;
        limit?: number;
        offset?: number;
        trash?: boolean;
        collection?: string;
      }) => {
        const qs = new URLSearchParams();
        if (params?.type && params.type !== 'all') qs.set('type', params.type);
        if (params?.folder) qs.set('folder', params.folder);
        if (params?.collection) qs.set('collection', params.collection);
        if (params?.limit) qs.set('limit', String(params.limit));
        if (params?.offset) qs.set('offset', String(params.offset));
        if (params?.trash) qs.set('trash', '1');
        return request<any>(`/api/admin/media?${qs}`);
      },
      collections: {
        list: () => request<any[]>('/api/admin/media/collections'),
        create: (name: string) => request<any>('/api/admin/media/collections', { method: 'POST', body: { name } }),
        delete: (id: string) => request<any>(`/api/admin/media/collections/${id}`, { method: 'DELETE' }),
        items: (id: string) => request<any[]>(`/api/admin/media/collections/${id}/items`),
        setItems: (id: string, media_ids: string[]) =>
          request<any>(`/api/admin/media/collections/${id}/items`, { method: 'PUT', body: { media_ids } }),
      },
      tags: {
        list: () => request<any[]>('/api/admin/media/tags'),
        create: (name: string) => request<any>('/api/admin/media/tags', { method: 'POST', body: { name } }),
      },
      usage: (id: string) => request<{ portfolio_projects: number; collections: number }>(`/api/admin/media/${id}/usage`),
      replace: async (id: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const token = localStorage.getItem('sb-token');
        const res = await fetch(`${BASE}/api/admin/media/${id}/replace`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        if (!res.ok) throw new Error('Replace failed');
        return res.json();
      },
      trash: () => request<any>('/api/admin/media/trash'),
      restore: (ids: string[]) => request<any>('/api/admin/media/restore', { method: 'POST', body: { ids } }),
      get: (id: string) => request<any>(`/api/admin/media/${id}`),
      playbackUrl: (id: string) => request<{ url: string }>(`/api/admin/media/${id}/playback-url`),
      ensurePoster: (id: string, time_sec?: number) =>
        request<{ video: any; poster_id: string | null }>(`/api/admin/media/${id}/ensure-poster`, {
          method: 'POST',
          body: time_sec != null ? { time_sec } : {},
        }),
      posterFrame: (id: string, time_sec: number, alt?: string) =>
        request<{ cover_media_id: string; public_url: string }>(`/api/admin/media/${id}/poster-frame`, {
          method: 'POST',
          body: { time_sec, alt },
        }),
      signedUrl: (id: string) => request<{ url: string; expires_in: number }>(`/api/admin/media/${id}/signed-url`),
      folders: () => request<any[]>('/api/admin/media/folders'),
      createFolder: (name: string, parent_id?: string) =>
        request<any>('/api/admin/media/folders', { method: 'POST', body: { name, parent_id } }),
      update: (id: string, data: any) => request<any>(`/api/admin/media/${id}`, { method: 'PUT', body: data }),
      delete: (id: string) => request<any>(`/api/admin/media/${id}`, { method: 'DELETE' }),
      bulkDelete: (ids: string[]) =>
        request<any>('/api/admin/media/bulk-delete', { method: 'POST', body: { ids } }),
      upload: async (file: File, alt?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        if (alt) formData.append('alt', alt);

        const token = localStorage.getItem('sb-token');
        const res = await fetch(`${BASE}/api/admin/media/upload`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        if (!res.ok) throw new Error('Upload failed');
        return res.json();
      },
    },
    cms: {
      pages: {
        list: () => request<any[]>('/api/admin/cms/pages'),
        create: (data: any) => request<any>('/api/admin/cms/pages', { method: 'POST', body: data }),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/pages/${id}`, { method: 'PUT', body: data }),
        delete: (id: string) => request<any>(`/api/admin/cms/pages/${id}`, { method: 'DELETE' }),
      },
      services: {
        list: () => request<any[]>('/api/admin/cms/services'),
        create: (data: any) => request<any>('/api/admin/cms/services', { method: 'POST', body: data }),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/services/${id}`, { method: 'PUT', body: data }),
        delete: (id: string) => request<any>(`/api/admin/cms/services/${id}`, { method: 'DELETE' }),
        reorder: (items: { id: string; sort_order: number }[]) =>
          request<any>('/api/admin/cms/services/reorder', { method: 'PUT', body: { items } }),
      },
      categories: {
        list: () => request<any[]>('/api/admin/cms/categories'),
        create: (data: any) => request<any>('/api/admin/cms/categories', { method: 'POST', body: data }),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/categories/${id}`, { method: 'PUT', body: data }),
        delete: (id: string) => request<any>(`/api/admin/cms/categories/${id}`, { method: 'DELETE' }),
      },
      faq: {
        list: () => request<any[]>('/api/admin/cms/faq'),
        create: (data: any) => request<any>('/api/admin/cms/faq', { method: 'POST', body: data }),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/faq/${id}`, { method: 'PUT', body: data }),
        delete: (id: string) => request<any>(`/api/admin/cms/faq/${id}`, { method: 'DELETE' }),
      },
      social: {
        list: () => request<any[]>('/api/admin/cms/social'),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/social/${id}`, { method: 'PUT', body: data }),
      },
      navigation: {
        list: () => request<any[]>('/api/admin/cms/navigation'),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/navigation/${id}`, { method: 'PUT', body: data }),
      },
      testimonials: {
        list: () => request<any[]>('/api/admin/cms/testimonials'),
        create: (data: any) => request<any>('/api/admin/cms/testimonials', { method: 'POST', body: data }),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/testimonials/${id}`, { method: 'PUT', body: data }),
        delete: (id: string) => request<any>(`/api/admin/cms/testimonials/${id}`, { method: 'DELETE' }),
      },
      footer: {
        list: () => request<any[]>('/api/admin/cms/footer'),
        create: (data: any) => request<any>('/api/admin/cms/footer', { method: 'POST', body: data }),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/footer/${id}`, { method: 'PUT', body: data }),
      },
      steps: {
        list: () => request<any[]>('/api/admin/cms/steps'),
        create: (data: any) => request<any>('/api/admin/cms/steps', { method: 'POST', body: data }),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/steps/${id}`, { method: 'PUT', body: data }),
        delete: (id: string) => request<any>(`/api/admin/cms/steps/${id}`, { method: 'DELETE' }),
      },
      contacts: {
        list: () => request<any[]>('/api/admin/cms/contacts'),
        update: (data: Record<string, string>) => request<any>('/api/admin/cms/contacts', { method: 'PUT', body: data }),
      },
      sections: {
        create: (data: any) => request<any>('/api/admin/cms/sections', { method: 'POST', body: data }),
        update: (id: string, data: any) => request<any>(`/api/admin/cms/sections/${id}`, { method: 'PUT', body: data }),
        delete: (id: string) => request<any>(`/api/admin/cms/sections/${id}`, { method: 'DELETE' }),
        reorder: (items: { id: string; sort_order: number }[]) =>
          request<any>('/api/admin/cms/sections/reorder', { method: 'PUT', body: { items } }),
      },
    },
    leads: {
      stats: () => request<{ by_status: Record<string, number>; total: number }>('/api/admin/leads/stats'),
      list: (params?: { status?: string; limit?: number; offset?: number }) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.set('status', params.status);
        if (params?.limit) qs.set('limit', String(params.limit));
        return request<any>(`/api/admin/leads?${qs}`);
      },
      get: (id: string) => request<any>(`/api/admin/leads/${id}`),
      history: (id: string) => request<any[]>(`/api/admin/leads/${id}/history`),
      update: (id: string, data: any) => request<any>(`/api/admin/leads/${id}`, { method: 'PUT', body: data }),
      addComment: (id: string, body: string) =>
        request<any>(`/api/admin/leads/${id}/comment`, { method: 'POST', body: { body } }),
      submit: (data: any) => request<any>('/api/public/leads', { method: 'POST', body: data }),
    },
    users: {
      list: () => request<any[]>('/api/admin/users'),
      assignees: () => request<any[]>('/api/admin/users/assignees'),
      create: (data: any) => request<any>('/api/admin/users', { method: 'POST', body: data }),
    },
    audit: {
      list: (params?: { limit?: number; entity_type?: string; entity_id?: string }) => {
        const qs = new URLSearchParams();
        qs.set('limit', String(params?.limit || 100));
        if (params?.entity_type) qs.set('entity_type', params.entity_type);
        if (params?.entity_id) qs.set('entity_id', params.entity_id);
        return request<any[]>(`/api/admin/audit?${qs}`);
      },
    },
    design: {
      get: () => request<any>('/api/admin/design'),
      update: (settings: any, status?: string) =>
        request<any>('/api/admin/design', { method: 'PUT', body: { settings, status } }),
      versions: () => request<any[]>('/api/admin/design/versions'),
      rollback: (versionId: string) =>
        request<any>(`/api/admin/design/rollback/${versionId}`, { method: 'POST' }),
    },
  },
};